import type { RiddleCategory } from './riddle-things.js'

/**
 * What a clue means, and nothing else.
 *
 * This file is the single definition of meaning in Rocky's Riddles. The
 * generator does not know what a clue says — it enumerates every clue that
 * could be written and asks `matches` which of them happen to be true of the
 * answer it dealt. The solver asks the same function.
 *
 * That is the whole design, and it is here because of a bug that shipped in
 * Turtley's Trolley: a question that asked "how much more did you spend?" of
 * the smaller basket. The sums were right and the sentence was a lie, and
 * every test passed, because the code that asked the question and the code
 * that marked the answer agreed with each other and both were wrong.
 *
 * A clue built to be true cannot catch that. A clue enumerated blind and then
 * filtered can, because the filter is the same reading the solver uses — so if
 * the reading is wrong, the riddle comes out with two answers or none, and the
 * sweep says so.
 */

/**
 * Which thing each teddy has, per category.
 *
 * `assignment[category][seat]` is a thing key. A seat is a position in the row
 * as it is drawn: seat 0 is the front. The row is **given** — she can see it —
 * so nothing here has to be deduced twice.
 */
export type Assignment = Readonly<Record<string, readonly string[]>>

export type ClueKind = 'direct' | 'negative' | 'either' | 'end' | 'neighbour' | 'link' | 'side'

export type Clue =
  /** "Sky took the pear." */
  | {
      readonly kind: 'direct'
      readonly seat: number
      readonly category: string
      readonly thing: string
    }
  /** "Hoppity did not take the apple." */
  | {
      readonly kind: 'negative'
      readonly seat: number
      readonly category: string
      readonly thing: string
    }
  /** "Sky or Bluey took the pear." */
  | {
      readonly kind: 'either'
      readonly seats: readonly [number, number]
      readonly category: string
      readonly thing: string
    }
  /** "The one with the pear sat at the front." */
  | {
      readonly kind: 'end'
      readonly end: 'front' | 'back'
      readonly category: string
      readonly thing: string
    }
  /** "The one with the pear sat next to Monkey." */
  | {
      readonly kind: 'neighbour'
      readonly seat: number
      readonly category: string
      readonly thing: string
    }
  /** "The one with the apple wore the red hat." */
  | {
      readonly kind: 'link'
      readonly category: string
      readonly thing: string
      readonly otherCategory: string
      readonly otherThing: string
    }
  /**
   * "Monkey sat somewhere behind the one with the pear." (`behind: 'teddy'`)
   * "The one with the pear sat somewhere behind Monkey." (`behind: 'thing'`)
   */
  | {
      readonly kind: 'side'
      readonly seat: number
      readonly behind: 'teddy' | 'thing'
      readonly category: string
      readonly thing: string
    }

/**
 * Where a thing is, in seats.
 *
 * Throws rather than returning -1. A thing that is in no seat means the
 * assignment is not a bijection, which is a broken puzzle rather than a false
 * clue, and the two must not come back as the same answer.
 */
export function holderOf(assignment: Assignment, category: string, thing: string): number {
  const row = assignment[category]
  if (!row) throw new Error(`No category ${category} in this assignment`)

  const seat = row.indexOf(thing)
  if (seat < 0) throw new Error(`Nobody has ${thing} in category ${category}`)

  return seat
}

/**
 * Whether a clue is true of an assignment.
 *
 * An exhaustive switch with no default, so a new clue kind fails to compile
 * here — which is one of exactly two places that must change to add one, the
 * other being `clueSentence`.
 */
export function matches(clue: Clue, assignment: Assignment): boolean {
  switch (clue.kind) {
    case 'direct':
      return holderOf(assignment, clue.category, clue.thing) === clue.seat

    case 'negative':
      return holderOf(assignment, clue.category, clue.thing) !== clue.seat

    case 'either':
      return clue.seats.includes(holderOf(assignment, clue.category, clue.thing))

    case 'end': {
      const row = assignment[clue.category]
      if (!row) throw new Error(`No category ${clue.category} in this assignment`)

      const at = clue.end === 'front' ? 0 : row.length - 1
      return holderOf(assignment, clue.category, clue.thing) === at
    }

    case 'neighbour':
      // Exactly one seat apart. A thing is never next to its own holder, which
      // is why the negation of this clue is not in the game: "the pear was not
      // next to Monkey" has two readings when Monkey is holding it, and no
      // wording under ten words fixes that. See the note in riddle-skills.
      return Math.abs(holderOf(assignment, clue.category, clue.thing) - clue.seat) === 1

    case 'link':
      return (
        holderOf(assignment, clue.category, clue.thing) ===
        holderOf(assignment, clue.otherCategory, clue.otherThing)
      )

    case 'side': {
      const holder = holderOf(assignment, clue.category, clue.thing)
      // Seat 0 is the front of the row, so "behind" is a higher seat number.
      return clue.behind === 'thing' ? holder > clue.seat : holder < clue.seat
    }
  }
}

/** Every clue in a set, true at once. */
export function satisfies(clues: readonly Clue[], assignment: Assignment): boolean {
  return clues.every((clue) => matches(clue, assignment))
}

/**
 * A stable identity for a clue, so a set can hold no duplicates.
 *
 * JSON of the clue's own fields, with the kind first. Two clues that say the
 * same thing in the same words have the same key; nothing here depends on
 * field order, because every clue of a kind is built the same way.
 */
export function clueKey(clue: Clue): string {
  return JSON.stringify(clue)
}

/**
 * How many assignments a puzzle of this shape has.
 *
 * `(teddies!)^categories`. It is checked before anything enumerates them,
 * because the failure otherwise is her tablet going quiet at the start of a
 * round rather than an error anybody sees.
 */
export function assignmentSpace(teddies: number, categories: number): number {
  let factorial = 1
  for (let n = 2; n <= teddies; n += 1) factorial *= n

  return factorial ** categories
}

/**
 * The largest search this game will run.
 *
 * Five teddies and two categories is 14,400, which is the biggest shape the
 * difficulty ladder can ask for. The cap is above that and below anything that
 * would be felt, so a third category or a sixth teddy trips it — and a test
 * enumerates every shape and checks, so it trips at build time rather than in
 * her hands.
 */
export const ASSIGNMENT_CAP = 20_000

/** Every ordering of a list. */
export function permutations<T>(items: readonly T[]): T[][] {
  if (items.length <= 1) return [[...items]]

  const out: T[][] = []
  for (const [index, item] of items.entries()) {
    const rest = [...items.slice(0, index), ...items.slice(index + 1)]
    for (const tail of permutations(rest)) out.push([item, ...tail])
  }

  return out
}

/**
 * Every way the things could be dealt out.
 *
 * Brute force, and deliberately so. Five teddies is 120 deals and the
 * correctness of the entire game rests on this function being right; fifteen
 * lines anybody can check beat a constraint propagator nobody can.
 */
export function allAssignments(
  categories: readonly RiddleCategory[],
  teddies: number,
): Assignment[] {
  const space = assignmentSpace(teddies, categories.length)
  if (space > ASSIGNMENT_CAP) {
    throw new Error(`A riddle of this shape has ${space} deals, over the cap of ${ASSIGNMENT_CAP}`)
  }

  let built: Assignment[] = [{}]

  for (const category of categories) {
    const deals = permutations(category.things.slice(0, teddies).map((thing) => thing.key))

    built = built.flatMap((partial) => deals.map((deal) => ({ ...partial, [category.key]: deal })))
  }

  return built
}

/** Every assignment the clues allow. One means the riddle is fair. */
export function solve(
  categories: readonly RiddleCategory[],
  teddies: number,
  clues: readonly Clue[],
): Assignment[] {
  return allAssignments(categories, teddies).filter((assignment) => satisfies(clues, assignment))
}
